import { useTranslation } from 'react-i18next';
import useIsHubUser from '@/hooks/useIsHubUser';
import { useGetEmployeeFromDirectoryQuery } from '@/redux/slices/employeeApiSlice';
import { LinkNoUnderline } from '@common';
import { Skeleton, Text } from '@electricjs/arc';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  AssetEventSource,
  EmployeeStatusEnum,
} from '@electricjs/core_entity-client';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { getDisplayFullName } from 'common/utils/getFullName';

type PerformedByCellProps = {
  performedBy?: string;
  eventSource?: AssetEventSource;
};

const PerformedByCell = ({
  performedBy,
  eventSource,
}: PerformedByCellProps) => {
  const { t } = useTranslation('assets');
  const organizationId = useGetOrganizationId();
  const params =
    eventSource === AssetEventSource.AssetLog && performedBy
      ? {
          employeeId: performedBy,
          organizationId,
        }
      : skipToken;
  const {
    data: employee,
    isLoading,
    error,
  } = useGetEmployeeFromDirectoryQuery(params);
  const isBasicHubUser = useIsHubUser();

  if (isLoading) {
    return <Skeleton count={1} />;
  }

  // Directory Insight, performedBy is an email
  if (eventSource === AssetEventSource.Jumpcloud && performedBy) {
    return <Text variant="body-2">{performedBy}</Text>;
  }

  // Asset Log, performedBy is an employee id
  if (error || !employee) {
    return (
      <Text variant="body-2">{t('eventsHistory.performedBy.electric')}</Text>
    );
  }

  if (isBasicHubUser || employee.status === EmployeeStatusEnum.Unknown) {
    return <Text variant="body-2">{getDisplayFullName(employee)}</Text>;
  }

  return (
    <LinkNoUnderline variant="body-2" href={`/employees/${performedBy}`}>
      {getDisplayFullName(employee)}
    </LinkNoUnderline>
  );
};

export default PerformedByCell;
